/**
 * Calculate time until interview and return formatted string
 * @param date - Interview date in YYYY-MM-DD format
 * @param time - Interview time in HH:MM format
 * @returns Formatted time string or empty string
 */
export const getTimeUntilInterview = (date?: string, time?: string): string => {
  if (!date || !time) return '';

  try {
    const interviewDateTime = new Date(`${date}T${time}:00`);
    const now = new Date();
    const diffMs = interviewDateTime.getTime() - now.getTime();

    // If interview is in the past, return empty string
    if (diffMs < 0) return '';

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays >= 1) {
      return `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    } else if (diffHours >= 1) {
      return `in ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
    } else if (diffMinutes >= 1) {
      return `in ${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`;
    }

    // If less than 1 minute, return empty string
    return '';
  } catch (error) {
    console.error('Error calculating time until interview:', error);
    return '';
  }
};

/**
 * Get Date object for interview datetime
 * @param date - Interview date in YYYY-MM-DD format
 * @param time - Interview time in HH:MM format
 * @returns Date object or null if invalid
 */
export const getInterviewDateTime = (date?: string, time?: string): Date | null => {
  if (!date || !time) return null;

  try {
    return new Date(`${date}T${time}:00`);
  } catch (error) {
    console.error('Error parsing interview datetime:', error);
    return null;
  }
};

/**
 * Check if interview is in the past
 * @param date - Interview date in YYYY-MM-DD format
 * @param time - Interview time in HH:MM format
 * @returns True if interview is in the past
 */
export const isPastInterview = (date?: string, time?: string): boolean => {
  const interviewDateTime = getInterviewDateTime(date, time);
  if (!interviewDateTime) return false;

  const now = new Date();
  return interviewDateTime < now;
};

/**
 * Check if interview is in the future
 * @param date - Interview date in YYYY-MM-DD format
 * @param time - Interview time in HH:MM format
 * @returns True if interview is in the future
 */
export const isFutureInterview = (date?: string, time?: string): boolean => {
  const interviewDateTime = getInterviewDateTime(date, time);
  if (!interviewDateTime) return false;

  const now = new Date();
  return interviewDateTime > now;
};

/**
 * Get relative time string for interview
 * @param date - Interview date in YYYY-MM-DD format
 * @param time - Interview time in HH:MM format
 * @returns Relative time string ("X days ago" or "in X days")
 */
export const getRelativeTimeString = (date?: string, time?: string): string => {
  const interviewDateTime = getInterviewDateTime(date, time);
  if (!interviewDateTime) return '';

  const now = new Date();
  const diffMs = interviewDateTime.getTime() - now.getTime();
  const diffMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMs <= 0) {
  // Past or exactly now
    if (diffDays >= 1) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffHours >= 1) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffMinutes >= 1) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    return 'just now';
  } else {
    // Future
    if (diffDays >= 1) {
      return `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    } else if (diffHours >= 1) {
      return `in ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
    } else if (diffMinutes >= 1) {
      return `in ${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`;
    }
    return 'soon';
  }
};
